import React from 'react';
import { View, Text, StyleSheet, Pressable, ViewStyle, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Colors, Fonts, Radii, Shadows, Spacing, Typography } from '../constants';
import { ClubMark } from './ClubBadge';
import { ClubLogo } from './ClubLogo';
import { Button } from './Button';

interface ClubCardProps {
  club: {
    id?: string;
    name: string;
    logo_url?: string | null;
    location?: string | null;
    /** Optional member count shown after the location */
    members?: number;
  };
  /** Shows a linen "Member" tag instead of the Join button */
  joined?: boolean;
  onPress?: () => void;
  onJoin?: () => void;
  joinLoading?: boolean;
  /** Row layout (mobile) instead of a stacked tile */
  mobile?: boolean;
  style?: ViewStyle;
}

/**
 * Club tile (design v2): crest or pastel mark, name, location · members,
 * then "Join club" (small outline button) or a linen "Member" tag.
 */
export const ClubCard: React.FC<ClubCardProps> = ({
  club,
  joined = false,
  onPress,
  onJoin,
  joinLoading = false,
  mobile = false,
  style,
}) => {
  const { t } = useTranslation();
  const markSize = mobile ? 48 : 56;
  const meta = [
    club.location || null,
    club.members != null ? t('screens.clubs.memberCount', { count: club.members }) : null,
  ].filter(Boolean);

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      style={({ hovered, pressed }: any) => [
        styles.card,
        mobile ? styles.cardRow : styles.cardTile,
        hovered && Shadows.cardHover,
        pressed && styles.pressed,
        style,
      ]}
    >
      {club.logo_url ? (
        <ClubLogo logoUrl={club.logo_url} size={markSize} borderRadius={Radii.thumb} />
      ) : (
        <ClubMark name={club.name} size={markSize} />
      )}
      <View style={styles.info}>
        <Text style={[Typography.cardTitle, styles.name]} numberOfLines={1}>{club.name}</Text>
        {meta.length > 0 && (
          <View style={styles.metaRow}>
            <Ionicons name="location-outline" size={13} color={Colors.textMedium} />
            <Text style={[Typography.meta, styles.meta]} numberOfLines={1}>{meta.join(' · ')}</Text>
          </View>
        )}
      </View>
      {joined ? (
        <View style={[styles.memberTag, !mobile && styles.memberTagTile]}>
          <Ionicons name="checkmark" size={14} color={Colors.textMedium} />
          <Text style={styles.memberTagText}>{t('enums.clubRole.member')}</Text>
        </View>
      ) : onJoin ? (
        <Button
          title={t('screens.clubDetail.joinClub')}
          size="sm"
          variant="outline"
          onPress={onJoin}
          loading={joinLoading}
          style={!mobile ? styles.joinTile : undefined}
        />
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.white,
    borderRadius: Radii.card,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    gap: Spacing.md,
    ...Shadows.card,
    ...(Platform.OS === 'web' && { cursor: 'pointer', touchAction: 'pan-y' } as any),
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
  },
  cardTile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 18,
  },
  pressed: {
    opacity: 0.9,
  },
  info: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
  },
  name: {
    color: Colors.text,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    marginTop: 3,
  },
  meta: {
    color: Colors.textMedium,
    flexShrink: 1,
  },
  memberTag: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 34,
    paddingHorizontal: 14,
    borderRadius: Radii.control,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  memberTagTile: {
    alignSelf: 'stretch',
  },
  memberTagText: {
    fontFamily: Fonts.semiBold,
    fontSize: 13,
    color: Colors.textMedium,
  },
  joinTile: {
    alignSelf: 'stretch',
  },
});
